import { useMemo, useState } from "react";

import "./OperatorUtilizationTable.scss";

const safeNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const cleanText = (value = "") =>
  String(value).replace(/\s+/g, " ").trim() || "-";

const formatNumber = (value, maximumFractionDigits = 2) =>
  new Intl.NumberFormat("en-IN", {
    maximumFractionDigits,
  }).format(safeNumber(value));

function OperatorUtilizationTable({
  operators = [],
  title = "Operator Performance Directory",
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("production");

  const filteredOperators = useMemo(() => {
    const search = searchTerm.trim().toLowerCase();

    const filtered = operators.filter((operator) => {
      const matchesSearch =
        !search ||
        cleanText(operator?.operatorUserName).toLowerCase().includes(search) ||
        cleanText(operator?.operatorUserId).toLowerCase().includes(search) ||
        (operator?.departments || []).some((department) =>
          cleanText(department?.deptName).toLowerCase().includes(search),
        ) ||
        (operator?.machines || []).some((machine) =>
          cleanText(machine?.machineCode).toLowerCase().includes(search),
        );

      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "utilized" && operator?.isUtilized) ||
        (statusFilter === "non-utilized" && !operator?.isUtilized);

      return matchesSearch && matchesStatus;
    });

    return [...filtered].sort((a, b) => {
      if (sortBy === "utilization") {
        return (
          safeNumber(b?.durationUtilization) -
          safeNumber(a?.durationUtilization)
        );
      }

      if (sortBy === "quality") {
        return (
          safeNumber(b?.qualityPercentage) - safeNumber(a?.qualityPercentage)
        );
      }

      return safeNumber(b?.totalProduced) - safeNumber(a?.totalProduced);
    });
  }, [operators, searchTerm, statusFilter, sortBy]);

  return (
    <section className="operator-utilization-table-card">
      <div className="operator-utilization-table-card__header">
        <div>
          <span>DETAILED WORKFORCE VIEW</span>
          <h3>{title}</h3>
          <p>
            Search operators, compare production and review department, machine,
            duration and quality metrics.
          </p>
        </div>

        <strong>{filteredOperators.length} operators</strong>
      </div>

      <div className="operator-utilization-table-toolbar">
        <label className="operator-utilization-search">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="11" cy="11" r="7" />
            <path d="m20 20-4-4" />
          </svg>
          <input
            type="search"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Search operator, ID, department or machine..."
          />
        </label>

        <select
          value={statusFilter}
          onChange={(event) => setStatusFilter(event.target.value)}
          aria-label="Filter operator status"
        >
          <option value="all">All operators</option>
          <option value="utilized">Utilized only</option>
          <option value="non-utilized">Non-utilized only</option>
        </select>

        <select
          value={sortBy}
          onChange={(event) => setSortBy(event.target.value)}
          aria-label="Sort operators"
        >
          <option value="production">Sort: Production</option>
          <option value="utilization">Sort: Utilization</option>
          <option value="quality">Sort: Quality</option>
        </select>
      </div>

      <div className="operator-utilization-table-scroll">
        <table>
          <thead>
            <tr>
              <th>Operator</th>
              <th>Status</th>
              <th>Department / Machine</th>
              <th>Jobs</th>
              <th>Produced</th>
              <th>OK / Reject / Rework</th>
              <th>Quality</th>
              <th>Duration</th>
              <th>Efficiency</th>
            </tr>
          </thead>

          <tbody>
            {filteredOperators.map((operator) => {
              const departmentNames = (operator?.departments || [])
                .map((department) => cleanText(department?.deptName))
                .filter(Boolean)
                .join(", ");

              const machineNames = (operator?.machines || [])
                .map((machine) => cleanText(machine?.machineCode))
                .filter(Boolean)
                .join(", ");

              return (
                <tr key={operator?.operatorUserId || operator?.operatorUserName}>
                  <td>
                    <div className="operator-person-cell">
                      <span className="operator-avatar">
                        {cleanText(operator?.operatorUserName).charAt(0)}
                      </span>
                      <div>
                        <strong>{cleanText(operator?.operatorUserName)}</strong>
                        <small>
                          {cleanText(operator?.operatorUserId)} •{" "}
                          {cleanText(operator?.userCategory)}
                        </small>
                      </div>
                    </div>
                  </td>

                  <td>
                    <span
                      className={`operator-status-pill ${
                        operator?.isUtilized ? "is-active" : "is-idle"
                      }`}
                    >
                      <i />
                      {operator?.isUtilized ? "Utilized" : "Non-utilized"}
                    </span>
                  </td>

                  <td>
                    <div className="operator-location-cell">
                      <strong>{departmentNames || "-"}</strong>
                      <small>{machineNames || "No machine"}</small>
                    </div>
                  </td>

                  <td>{formatNumber(operator?.totalJobs, 0)}</td>

                  <td>
                    <strong className="operator-table-number">
                      {formatNumber(operator?.totalProduced, 0)}
                    </strong>
                  </td>

                  <td>
                    <div className="operator-mix-cell">
                      <span className="ok">
                        {formatNumber(operator?.okQty, 0)}
                      </span>
                      <span className="reject">
                        {formatNumber(operator?.rejectQty, 0)}
                      </span>
                      <span className="rework">
                        {formatNumber(operator?.reworkQty, 0)}
                      </span>
                    </div>
                  </td>

                  <td>
                    <div className="operator-progress-cell">
                      <strong>
                        {formatNumber(operator?.qualityPercentage)}%
                      </strong>
                      <span>
                        <i
                          style={{
                            width: `${Math.min(
                              100,
                              Math.max(
                                0,
                                safeNumber(operator?.qualityPercentage),
                              ),
                            )}%`,
                          }}
                        />
                      </span>
                    </div>
                  </td>

                  <td>
                    <div className="operator-progress-cell duration">
                      <strong>
                        {formatNumber(operator?.durationUtilization)}%
                      </strong>
                      <span>
                        <i
                          style={{
                            width: `${Math.min(
                              100,
                              Math.max(
                                0,
                                safeNumber(operator?.durationUtilization),
                              ),
                            )}%`,
                          }}
                        />
                      </span>
                    </div>
                  </td>

                  <td>
                    <span className="operator-efficiency-pill">
                      {formatNumber(operator?.efficiency)}%
                    </span>
                  </td>
                </tr>
              );
            })}

            {!filteredOperators.length && (
              <tr>
                <td colSpan="9" className="operator-table-empty">
                  No operators match your current filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default OperatorUtilizationTable;
